import React, { useState, useEffect, useRef } from "react";
import "../styles/EmailVerify.css";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import HeaderLogo from "../components/HeaderLogo";
import ApiService from "../services/api";
import { EmailVerifyGuard } from "../components/RouteGuard";

function EmailVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputsRef = useRef([]);
  const [error, setError] = useState("");

  // Get email and purpose from navigation state or localStorage
  const email = location.state?.email || localStorage.getItem('reset_email');
  const purpose = location.state?.purpose || 'password_reset';

  // Redirect if no email found
  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
      return;
    }
  }, [email, navigate]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const handleResend = async () => {
    if (!canResend || !email) return;

    setResendLoading(true);
    setError("");

    try {
      let response;
      if (purpose === 'password_reset') {
        response = await ApiService.resendPasswordResetOTP(email);
      } else if (purpose === 'superadmin_login') {
        response = await ApiService.resendLoginOTP(email);
      }
      
      setTimeLeft(60);
      setCanResend(false);
      setOtp(new Array(6).fill(""));
      inputsRef.current[0]?.focus();
      
      // Show success message
      setError(""); // Clear any previous errors
      // You could show a success toast here
    } catch (error) {
      setError(error.message || "Failed to resend code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (/^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError("");

      if (index < otp.length - 1) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      setError("");
      inputsRef.current[otp.length - 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        if (index > 0) inputsRef.current[index - 1].focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");
    
    if (enteredOtp.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (purpose === 'password_reset') {
        // Verify password reset OTP
        await ApiService.verifyPasswordResetOTP(email, enteredOtp);
        
        // Set OTP verified flag
        localStorage.setItem('otp_verified', 'true');
        
        // Navigate to create password page
        navigate("/create-password", { 
          state: { 
            email: email,
            otp: enteredOtp 
          }
        });
      } else if (purpose === 'superadmin_login') {
        // Handle superadmin login OTP verification
        const userId = localStorage.getItem('login_user_id');
        const response = await ApiService.verifySuperAdminOTP(userId, enteredOtp);
        
        // Store token and redirect to dashboard
        ApiService.setToken(response.access_token);
        navigate("/dashboard");
      }
    } catch (error) {
      setError(error.message || "Invalid OTP. Please try again.");
      setOtp(new Array(6).fill(""));
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 50);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EmailVerifyGuard>
      <div className="email-container">
      <HeaderLogo />

      <div className="email-card">
        <div className="email-back" onClick={() => navigate("/forgot-password")}>
          <FaChevronLeft className="back-icon" />
          <span className="back-text">Back</span>
        </div>

        <h2 className="email-heading">Verify your email</h2>
        <p className="email-subtext">
          Please enter the 6 digit code sent to your email address
        </p>
        {email && <p className="email-email-display">{email}</p>}

        {/* OTP Inputs */}
        <div className="email-otp-boxes">
          {otp.map((digit, i) => (
            <input
              key={i}
              type="text"
              maxLength="1"
              value={digit}
              ref={(el) => (inputsRef.current[i] = el)}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              className="twofa-otp-input"
              disabled={isLoading}
            />
          ))}
          <p className="email-error">{error || " "}</p>
        </div>

        {/* Resend */}
        <div className="email-resend">
          Didn't get the code?
          <button
            onClick={handleResend}
            disabled={!canResend || resendLoading}
            className={`email-resend-btn ${canResend ? "active" : "disabled"}`}
          >
            {resendLoading ? "Resending..." : "Resend"}
          </button>
        </div>

        {/* Timer */}
        <div className={`email-timer-wrapper ${canResend ? "fade-out" : ""}`}>
          {!canResend && (
            <p className="email-timer">
              Time remaining:{" "}
              <span>
                {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:
                {String(timeLeft % 60).padStart(2, "0")}s
              </span>
            </p>
          )}
        </div>

        {/* Verify */}
        <button
          className="email-verify-btn"
          onClick={handleVerify}
          disabled={isLoading || otp.join("").length !== 6}
        >
          {isLoading ? "Verifying..." : "Verify"}
        </button>
      </div>
    </div>
    </EmailVerifyGuard>
    
  );
}

export default EmailVerify;
